import { useFlags } from "launchdarkly-react-client-sdk";
import * as React from "react";

import Link from "@mui/material/Link";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { APPOINTMENT_PAGE_REDESIGN } from "constants/feature-flags";
import { REVIEW_PARAM } from "features/sales-coach/sales-coach.constants";
import { AmbientScribeNotification } from "services/types/notifications";

type Destination = { href: string; label: string };

/**
 * Five different backend senders arrive as `NotificationType.AMBIENT_SCRIBE` (8), and
 * two keys in `extra` are all that separate them: `review_id` marks a Sales Coach
 * review (SN-19585) and `insight_count` marks scribe insights. Everything else is a
 * SOAP/summary/visit-summary note against the chart.
 *
 * A sales review points at the APPOINTMENT, because that is where the review is
 * rendered — `SalesCoachReviewSummaryCard` on the appointment page (SN-19586). It used
 * to fall through to the chart-notes default, which has no Sales Coach surface at all.
 *
 * ⚠️ `isAppointmentPageEnabled` is not optional garnish. `patient-appointments.routes.tsx`
 * picks between two different components for `/patients/:p/appointments/:a`, and the card
 * exists ONLY on the `appointmentPageRedesign` one — the legacy page has no Sales Coach
 * reference anywhere. With that flag off the appointment link is a dead end for every
 * user in the practice, so we fall back to the Sales Coach modal, which renders the
 * review regardless. The flag is reliably readable here: the row itself only renders once
 * `salesCoachDashboard` has resolved, and LaunchDarkly delivers a context's whole payload
 * in one go, so if one flag has landed they all have.
 *
 * ⚠️ Both URLs are built INLINE rather than imported from
 * `features/sales-coach/utils/sales-coach.links.ts`, which owns every Sales Coach URL and
 * already carries an `appointmentUrl` of its own. This component lives in the
 * always-mounted top bar, and that module would drag the lazy Sales Coach chunk into the
 * entry bundle. `features/follow-ups/follow-ups.util.ts` is no better — also lazy. So this
 * is the THIRD copy of the two-line appointment-URL builder; the other two are pinned
 * against each other by `features/sales-coach/__tests__/sales-coach.links.test.ts`, and
 * this one is pinned by the literal href in this component's own test. Change the URL
 * shape and all three move together.
 *
 * `REVIEW_PARAM` is imported instead of that same links module for the same reason:
 * `sales-coach.constants.ts` is the one file in that feature with no imports of its own,
 * so it is the one that cannot pull the section in behind it. Keep it that way.
 */
const resolveDestination = (
  extra: AmbientScribeNotification["extra"],
  isAppointmentPageEnabled: boolean
): Destination => {
  if (extra.review_id != null) {
    const { patient_id, appointment_id } = extra;

    /*
     * `!= null` rather than a truthy test, matching the two guards this file already
     * has (`review_id`, `insight_count` — the latter MUST be `!= null`, because
     * `insight_count: 0` is a real insights notification). So `0` counts as a present
     * id here too. Django autoincrement PKs start at 1, so that is unreachable in
     * practice; if it ever were not, this builds `/patients/0/appointments/0` rather
     * than falling back. Pinned by a test, so switching to truthy is a decision
     * someone makes on purpose rather than a silent drift.
     */
    if (
      isAppointmentPageEnabled &&
      patient_id != null &&
      appointment_id != null
    ) {
      return {
        href: `/patients/${patient_id}/appointments/${appointment_id}`,
        label: "Open Appointment"
      };
    }

    return {
      href: `/sales-coach/opportunities?${REVIEW_PARAM}=${extra.review_id}`,
      label: "Open Sales Review"
    };
  }

  if (extra.insight_count != null) {
    return {
      href: `/patients/${extra.patient_id}/scribe-insights`,
      label: "Open Scribe Insights"
    };
  }

  return {
    href: `/patients/${extra.patient_id}/chart-notes`,
    label: "Open Chart Notes"
  };
};

const AmbientScribeNotificationComponent = ({
  title,
  description,
  extra
}: AmbientScribeNotification) => {
  const flags = useFlags();
  const { href, label } = resolveDestination(
    extra,
    Boolean(flags[APPOINTMENT_PAGE_REDESIGN])
  );

  return (
    <ListItemText
      primary={title}
      secondary={
        <Stack component="span" gap={1}>
          <Typography component="span">{description}</Typography>
          <Link href={href} underline="hover">
            {label}
          </Link>
        </Stack>
      }
    />
  );
};

export default AmbientScribeNotificationComponent;
